import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Plus, Pencil, Trash2, Palette, Loader2, Copy, Check } from 'lucide-react'
import type {
  ColorRule,
  CreateColorRuleRequest,
  UpdateColorRuleRequest,
  ComplianceSeverity,
} from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface ColorPalettePanelProps {
  colorRules: ColorRule[]
  loading: boolean
  onAdd: (data: CreateColorRuleRequest) => Promise<void>
  onUpdate: (ruleId: string, data: UpdateColorRuleRequest) => Promise<void>
  onDelete: (ruleId: string) => Promise<void>
}

const ALLOWED_USAGES = [
  { value: 'text', label: 'Text' },
  { value: 'background', label: 'Background' },
  { value: 'border', label: 'Border' },
  { value: 'accent', label: 'Accent' },
  { value: 'icon', label: 'Icons' },
]

const defaultFormData: CreateColorRuleRequest = {
  name: '',
  hexValue: '#000000',
  isPrimary: false,
  isSecondary: false,
  allowedUsages: [],
  tolerancePercent: 5,
  severity: 'Warning',
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result || !result[1] || !result[2] || !result[3]) return ''
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

export function ColorPalettePanel({
  colorRules,
  loading,
  onAdd,
  onUpdate,
  onDelete,
}: ColorPalettePanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ColorRule | null>(null)
  const [deleteRule, setDeleteRule] = useState<ColorRule | null>(null)
  const [formData, setFormData] = useState<CreateColorRuleRequest>(defaultFormData)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [copiedColor, setCopiedColor] = useState<string | null>(null)

  const handleOpenDialog = (rule?: ColorRule) => {
    if (rule) {
      setEditingRule(rule)
      setFormData({
        name: rule.name,
        hexValue: rule.hexValue,
        isPrimary: rule.isPrimary,
        isSecondary: rule.isSecondary,
        allowedUsages: rule.allowedUsages,
        tolerancePercent: rule.tolerancePercent,
        severity: rule.severity,
      })
    } else {
      setEditingRule(null)
      setFormData(defaultFormData)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingRule(null)
    setFormData(defaultFormData)
  }

  const handleSave = async () => {
    if (!formData.name || !formData.hexValue) return

    setSaving(true)
    try {
      if (editingRule) {
        await onUpdate(editingRule.id, formData as UpdateColorRuleRequest)
      } else {
        await onAdd(formData)
      }
      handleCloseDialog()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteRule) return

    setDeleting(true)
    try {
      await onDelete(deleteRule.id)
      setDeleteRule(null)
    } finally {
      setDeleting(false)
    }
  }

  const toggleUsage = (usage: string) => {
    const current = formData.allowedUsages || []
    const updated = current.includes(usage)
      ? current.filter((u) => u !== usage)
      : [...current, usage]
    setFormData({ ...formData, allowedUsages: updated })
  }

  const copyToClipboard = async (text: string, colorId: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedColor(colorId)
    setTimeout(() => setCopiedColor(null), 2000)
  }

  const getSeverityBadge = (severity: ComplianceSeverity) => {
    const styles = {
      Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    }
    return <Badge className={cn('text-xs', styles[severity])}>{severity}</Badge>
  }

  const primaryColors = colorRules.filter((c) => c.isPrimary)
  const secondaryColors = colorRules.filter((c) => c.isSecondary && !c.isPrimary)
  const otherColors = colorRules.filter((c) => !c.isPrimary && !c.isSecondary)

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading color palette...</p>
      </div>
    )
  }

  const renderColorCard = (rule: ColorRule) => (
    <div
      key={rule.id}
      className="group relative bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden"
    >
      <div
        className="h-20 w-full"
        style={{ backgroundColor: rule.hexValue }}
      />
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100 truncate">
            {rule.name}
          </span>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => handleOpenDialog(rule)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteRule(rule)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => copyToClipboard(rule.hexValue, rule.id)}
            className="flex items-center gap-1 px-2 py-1 bg-neutral-100 dark:bg-neutral-700 rounded text-xs font-mono hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
          >
            {copiedColor === rule.id ? (
              <Check className="h-3 w-3 text-green-600" />
            ) : (
              <Copy className="h-3 w-3 text-neutral-400" />
            )}
            {rule.hexValue}
          </button>
          {getSeverityBadge(rule.severity)}
        </div>
        {rule.allowedUsages.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {rule.allowedUsages.map((usage) => (
              <span
                key={usage}
                className="text-xs px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-neutral-600 dark:text-neutral-300"
              >
                {usage}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Color Palette
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Define brand colors and their allowed usages
          </p>
        </div>
        <Button size="sm" onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-1" />
          Add Color
        </Button>
      </div>

      {colorRules.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
          <Palette className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No colors defined</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            Add brand colors to enforce color consistency
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Primary Colors */}
          {primaryColors.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Primary Colors
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {primaryColors.map(renderColorCard)}
              </div>
            </div>
          )}

          {/* Secondary Colors */}
          {secondaryColors.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Secondary Colors
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {secondaryColors.map(renderColorCard)}
              </div>
            </div>
          )}

          {/* Other Colors */}
          {otherColors.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Additional Colors
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {otherColors.map(renderColorCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Color' : 'Add Color'}</DialogTitle>
            <DialogDescription>
              Configure color name, value, and allowed usages.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Color Name</Label>
              <Input
                placeholder="e.g., Brand Blue"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Color Value</Label>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="color"
                    value={formData.hexValue}
                    onChange={(e) => setFormData({ ...formData, hexValue: e.target.value })}
                    className="w-12 h-12 rounded-lg border border-neutral-200 dark:border-neutral-700 cursor-pointer"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Input
                    value={formData.hexValue}
                    onChange={(e) => setFormData({ ...formData, hexValue: e.target.value })}
                    placeholder="#000000"
                    className="font-mono"
                  />
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    RGB: {hexToRgb(formData.hexValue)}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <Label className="cursor-pointer">Primary</Label>
                <Switch
                  checked={formData.isPrimary}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isPrimary: checked, isSecondary: checked ? false : formData.isSecondary })
                  }
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <Label className="cursor-pointer">Secondary</Label>
                <Switch
                  checked={formData.isSecondary}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isSecondary: checked, isPrimary: checked ? false : formData.isPrimary })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Allowed Usages</Label>
              <div className="flex flex-wrap gap-2">
                {ALLOWED_USAGES.map((usage) => (
                  <button
                    key={usage.value}
                    type="button"
                    onClick={() => toggleUsage(usage.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-md border transition-colors',
                      formData.allowedUsages?.includes(usage.value)
                        ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-neutral-100 dark:text-neutral-900 dark:border-neutral-100'
                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-600'
                    )}
                  >
                    {usage.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color Tolerance (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.tolerancePercent}
                onChange={(e) =>
                  setFormData({ ...formData, tolerancePercent: parseInt(e.target.value) || 0 })
                }
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Allow slight color variations within this percentage
              </p>
            </div>

            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) =>
                  setFormData({ ...formData, severity: value as ComplianceSeverity })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Error">Error - Block non-compliant</SelectItem>
                  <SelectItem value="Warning">Warning - Show warning</SelectItem>
                  <SelectItem value="Info">Info - Informational only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.name || !formData.hexValue}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRule ? 'Save Changes' : 'Add Color'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteRule} onOpenChange={(open) => !open && setDeleteRule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Color</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteRule?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRule(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
